/*
 * Content enhancers: things that must run over every piece of server-rendered
 * HTML after it lands in the page (Bootstrap popovers and tooltips, datalist
 * autocompletes, ...). The old code called each area's Init() from an inline
 * <script> in the partial; here the swap itself announces the new content
 * (dom.swapHtml, modal.showModal and tabs.loadTab dispatch the events) and
 * every registered enhancer runs over it.
 */
export type Enhancer = (root: ParentNode) => void;

const enhancers: Enhancer[] = [];
let bound = false;

export function registerEnhancer(enhancer: Enhancer): void {
    if (!enhancers.includes(enhancer)) enhancers.push(enhancer);
}

/** Run every enhancer over a root (the whole document by default). */
export function enhance(root: ParentNode = document): void {
    for (const enhancer of enhancers) enhancer(root);
}

const onContent = (event: Event): void => {
    const target = event.target;
    if (target instanceof Element || target instanceof Document) enhance(target);
};

/** Bind once: swapped HTML, loaded tab panes, and the initial document. */
export function initEnhancers(): void {
    if (bound) return;
    bound = true;
    document.addEventListener('mcp:html-swapped', onContent);
    document.addEventListener('mcp:tab-loaded', onContent);
    const run = (): void => enhance(document);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
    else run();
}

/** Test support. */
export function resetEnhancers(): void {
    enhancers.length = 0;
    document.removeEventListener('mcp:html-swapped', onContent);
    document.removeEventListener('mcp:tab-loaded', onContent);
    bound = false;
}
